<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';
import type { GateEvent } from './api/client.js';
import ChangePasswordModal from './components/ChangePasswordModal.vue';
import Toasts from './components/Toasts.vue';
import { useSession } from './stores/session.js';
import { useSkin } from './stores/skin.js';
import { surfaceOf, useTenantStatus } from './stores/tenant.js';
import { applySiteIcon, siteIconFor } from './ui/site-icon.js';

/**
 * 站点闸门(设计图 24):接口回「维护中」就去维护页(考试端除外——考试接口维护期间照常);
 * 回「机构只读 / 配额已满」就刷新只读横幅。
 * 后台账号还在用初始密码:任何页面都先弹不可关的「请先修改密码」(原型 global/login)。
 */
const route = useRoute();
const router = useRouter();
const tenant = useTenantStatus();
const session = useSession();
const forceChange = computed(() => session.mustChangePassword && !!session.me && route.name !== 'login');

/**
 * 浏览器标签页上的站点图标跟着品牌走(规则在 ui/site-icon.ts):平台身份、没开白标的机构用平台图标;
 * 开了白标的机构用它自己的 Logo(按当前页面所属的面),没有 Logo 就不出图标 —— 白标站点不露出平台的图标。
 * 后台页看「后台白标」,其余页看「站点白标」。站点信息还没取到(或这一页不取站点信息)时先不出图标:
 * 不知道是不是白标机构,宁可空着也不露出平台的(index.html 里静态写的也是「不出」)。
 */
const siteIcon = computed(() => {
  if (!tenant.info) return siteIconFor(null);
  const b = tenant.info.brand;
  return siteIconFor({
    isOrg: !!b,
    whiteLabel: !!(route.path.startsWith('/admin') ? b?.whiteLabel?.admin : b?.whiteLabel?.site),
    logoUrl: tenant.faceOf(surfaceOf(route.path))?.logoUrl ?? null,
  });
});
watch(siteIcon, (icon) => applySiteIcon(document, icon), { immediate: true });

/**
 * 标签页标题也跟着品牌走:机构站点写机构在这一面上的显示名,平台站点写平台名。
 * 以前标题一直是 index.html 里写死的平台名,机构站点(包括开了白标的)的标签页上也是它。
 * 站点信息没取到之前不动:index.html 里的标题是空的(浏览器显示网址),不先露出平台名。
 */
const skin = useSkin();
const siteTitle = computed(() =>
  tenant.info ? (tenant.faceOf(surfaceOf(route.path))?.displayName ?? skin.brand) : '',
);
watch(
  siteTitle,
  (title) => {
    if (title) document.title = title;
  },
  { immediate: true },
);

/**
 * 第一次进页面要先等站点信息(品牌先于首屏,见 router 的守卫),平时一两百毫秒;网络慢的时候等的那一会儿
 * 给一个不带品牌的小转圈(过了半秒还没好才显出来,平时看不到),不让人对着白屏。第一个页面出来就撤掉。
 */
const booted = ref(false);

/**
 * 换页面时顶上一条细进度条:点了导航,新页面的分包下载 / 取数要几百毫秒,以前这段时间页面一动不动,像没点上。
 * 超过 120 毫秒还没好才显出来(快的切换看不到它)。
 */
const routing = ref(false);
let routingTimer = 0;
router.beforeEach(() => {
  window.clearTimeout(routingTimer);
  routingTimer = window.setTimeout(() => (routing.value = true), 120);
});
router.afterEach(() => {
  window.clearTimeout(routingTimer);
  routing.value = false;
});
router.onError(() => {
  window.clearTimeout(routingTimer);
  routing.value = false;
});
void router.isReady().finally(() => (booted.value = true));

function onGate(e: Event) {
  const g = (e as CustomEvent<GateEvent>).detail;
  if (g.code === 'S0003') {
    const name = String(route.name ?? '');
    if (name === 'maintenance' || name.startsWith('exam')) return;
    void router.replace({ name: 'maintenance', query: { until: g.data?.until ?? '', from: route.fullPath } });
  } else void tenant.load(true);
}
/** 接口层回 A0105(初始密码没改):不管哪个页面发的请求,都弹「请先修改密码」 */
function onPwc() {
  session.mustChangePassword = true;
}
onMounted(() => {
  window.addEventListener('cg:gate', onGate);
  window.addEventListener('cg:pwc', onPwc);
});
onBeforeUnmount(() => {
  window.removeEventListener('cg:gate', onGate);
  window.removeEventListener('cg:pwc', onPwc);
});
</script>

<template>
  <RouterView />
  <div v-if="!booted" class="app-boot" role="status" aria-label="正在加载"><i></i></div>
  <div v-if="booted && routing" class="app-routing" role="progressbar" aria-label="正在打开页面"><i></i></div>
  <Toasts />
  <ChangePasswordModal :open="forceChange" force />
</template>

<style scoped>
/* 第一个页面出来之前的小转圈:中性的灰,不带任何品牌色;晚半秒才显出来 */
.app-boot {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
  opacity: 0;
  animation: app-boot-in 0.3s 0.5s forwards;
}
.app-boot i {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid var(--line-2);
  border-top-color: var(--muted-3);
  animation: app-boot-spin 0.8s linear infinite;
}
/* 换页面的细进度条:贴在最顶上,主题色,从左往右慢慢走到九成等着 */
.app-routing {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  z-index: 9999;
  height: 3px;
  pointer-events: none;
}
.app-routing i {
  display: block;
  height: 100%;
  width: 90%;
  background: linear-gradient(90deg, var(--brand), var(--accent));
  box-shadow: 0 0 8px color-mix(in srgb, var(--brand) 60%, transparent);
  transform-origin: left center;
  animation: app-routing-go 2.4s cubic-bezier(0.1, 0.7, 0.2, 1) forwards;
}
@keyframes app-routing-go {
  from {
    scale: 0 1;
  }
}
@keyframes app-boot-in {
  to {
    opacity: 1;
  }
}
@keyframes app-boot-spin {
  to {
    rotate: 360deg;
  }
}
@media (prefers-reduced-motion: reduce) {
  .app-boot i {
    animation-duration: 2.4s;
  }
}
</style>
